import React, { useEffect, useState } from "react";
import { api, formatRupiah } from "@/lib/api";
import { PageHeader, Section, DataCard, Empty } from "@/components/Shared";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { useAuth } from "@/lib/auth";
import { AlertTriangle, TrendingUp } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setKpi(r.data)).catch(() => {});
  }, []);

  const materialColors = ["#FF6600", "#FFD700", "#16A34A", "#2563EB"];

  return (
    <div>
      <PageHeader
        title={`Selamat datang, ${user?.name?.split(" ")[0] || ""}`}
        subtitle="Ringkasan operasional real-time seluruh site"
        testId="dashboard-header"
      />
      <Section>
        {!kpi ? (
          <div className="text-sm text-neutral-500 font-mono">Memuat data...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="dashboard-kpi-grid">
              <DataCard label="Karyawan" value={kpi.users_count} testId="kpi-users" />
              <DataCard label="Absen Hari Ini" value={kpi.absensi_today} accent="orange" testId="kpi-absensi" />
              <DataCard label="Alat Berat" value={kpi.assets_count} testId="kpi-assets" />
              <DataCard label="Site Aktif" value={kpi.sites_count} testId="kpi-sites" />
              <DataCard label="Maintenance" value={kpi.maintenance_open} accent="yellow" testId="kpi-maintenance" />
              <DataCard label="Saldo Kas" value={formatRupiah(kpi.kas_balance)} accent={kpi.kas_balance >= 0 ? "green" : "red"} testId="kpi-kas" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
              <div className="lg:col-span-2 border border-neutral-800 bg-[#141414] p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">// PRODUKSI BULAN INI</div>
                    <h3 className="font-display font-black text-xl mt-1">Tonase per Material</h3>
                  </div>
                  <TrendingUp className="text-[#FF6600]" size={20} />
                </div>
                {kpi.produksi_by_material?.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={kpi.produksi_by_material}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                      <XAxis dataKey="material" stroke="#94A3B8" fontSize={12} />
                      <YAxis stroke="#94A3B8" fontSize={12} />
                      <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626" }} />
                      <Bar dataKey="tonase" fill="#FF6600" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty message="Belum ada data produksi bulan ini" />
                )}
              </div>

              <div className="border border-neutral-800 bg-[#141414] p-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">// EXCEPTION QUEUE</div>
                <h3 className="font-display font-black text-xl mb-4">Perlu Tindakan</h3>
                <div className="space-y-3">
                  <ExceptionRow label="Kasbon Pending" value={kpi.pending_kasbon} testId="exc-kasbon" />
                  <ExceptionRow label="PO/PR Pending" value={kpi.pending_po} testId="exc-po" />
                  <ExceptionRow label="Maintenance Open" value={kpi.maintenance_open} testId="exc-maint" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <DataCard label="Kas Masuk (total)" value={formatRupiah(kpi.kas_masuk)} accent="green" />
              <DataCard label="Kas Keluar (total)" value={formatRupiah(kpi.kas_keluar)} accent="red" />
              <DataCard label={`BBM Bulan Ini`} value={`${kpi.bbm_liter_month.toLocaleString('id-ID')} L`} hint={formatRupiah(kpi.bbm_cost_month)} accent="yellow" />
            </div>
          </>
        )}
      </Section>
    </div>
  );
}

function ExceptionRow({ label, value, testId }) {
  const urgent = value > 0;
  return (
    <div className="flex items-center justify-between border-l-2 border-neutral-800 pl-3 py-2" style={{ borderColor: urgent ? "#FF6600" : "#262626" }} data-testid={testId}>
      <div className="flex items-center gap-2">
        {urgent && <AlertTriangle size={14} className="text-[#FF6600]" />}
        <span className="text-sm">{label}</span>
      </div>
      <span className={`font-display font-black text-lg ${urgent ? "text-[#FF6600]" : "text-neutral-600"}`}>{value}</span>
    </div>
  );
}
